import { BenefitsAmounts } from 'src/app/models/patient-enrollment.model';
import {
  BenefitsBadgeColor,
  benefitsBadge,
  formatPercent,
  formatUsd,
  num,
  remainingOfTotal,
} from 'src/app/clinical/dashboards/patient-enrollments/benefits-check-display';
import { ManualBenefitsCheckResponse } from './manual-benefits-check.model';

export interface BenefitsResultLine {
  label: string;
  value: string;
}

export interface BenefitsResultGroup {
  title: string;
  lines: BenefitsResultLine[];
}

/** Everything the dialog draws, already in plain words. */
export interface BenefitsResultView {
  title: string;
  /** Covered / Not covered / Needs review chip; null when the outcome has no verdict to show. */
  verdict: { label: string; color: BenefitsBadgeColor } | null;
  /** The sentence the app adds on top of the backend message (checked recently, wait time). */
  notice: string | null;
  message: string;
  payerPlan: string | null;
  groups: BenefitsResultGroup[];
  asOf: string | null;
}

const VERDICT_LABELS: Record<string, string> = {
  eligible: 'Covered',
  ineligible: 'Not covered',
  needs_review: 'Needs review',
};

const TITLES: Record<string, string> = {
  checked: 'Benefits check',
  checked_recently: 'Benefits check',
  medicare_manual: 'Medicare needs a manual check',
  no_insurance: 'No insurance on file',
  unknown_clinic: "Can't check this clinic yet",
  rate_limited: 'Slow down a moment',
  in_progress: 'Check already running',
  error: "Couldn't run the check",
};

/** "about 2 minutes"; a missing or unreadable wait reads as "a minute". */
export const waitPhrase = (seconds: number | undefined): string => {
  if (typeof seconds !== 'number' || !isFinite(seconds) || seconds <= 0) {
    return 'a minute';
  }
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return minutes === 1 ? 'about 1 minute' : `about ${minutes} minutes`;
};

const date = (iso: string | undefined): Date | null => {
  const value = iso ? new Date(iso) : null;
  return value && !isNaN(value.getTime()) ? value : null;
};

const line = (label: string, value: string | null): BenefitsResultLine[] => (value ? [{ label, value }] : []);
const money = (value: unknown): string | null => (num(value) !== undefined ? formatUsd(value as number) : null);

/** A missing amount is left out; it is never drawn as $0. */
const amountGroups = (b: BenefitsAmounts): BenefitsResultGroup[] => {
  const groups: BenefitsResultGroup[] = [
    {
      title: 'Deductible',
      lines: [
        ...line('Individual', remainingOfTotal(num(b.individualDeductibleRemaining), num(b.individualDeductibleTotal))),
        ...line('Family', remainingOfTotal(num(b.familyDeductibleRemaining), num(b.familyDeductibleTotal))),
      ],
    },
    {
      title: 'Out-of-pocket maximum',
      lines: [
        ...line('Individual', remainingOfTotal(num(b.individualOopRemaining), num(b.individualOopTotal))),
        ...line('Family', remainingOfTotal(num(b.familyOopRemaining), num(b.familyOopTotal))),
      ],
    },
    {
      title: 'Visit costs',
      lines: [
        ...line('Office visit copay', money(b.officeVisitCopay)),
        ...line('Specialist copay', money(b.specialistCopay)),
        ...line(
          'Office visit coinsurance',
          num(b.officeVisitCoinsurance) !== undefined ? formatPercent(b.officeVisitCoinsurance) : null
        ),
        ...line(
          'Specialist coinsurance',
          num(b.specialistCoinsurance) !== undefined ? formatPercent(b.specialistCoinsurance) : null
        ),
      ],
    },
  ];
  return groups.filter((group) => group.lines.length > 0);
};

export const benefitsResultView = (response: ManualBenefitsCheckResponse): BenefitsResultView => {
  const { outcome, benefitsCheck } = response;
  const showsResult = outcome === 'checked' || outcome === 'checked_recently';
  const badge = showsResult ? benefitsBadge(benefitsCheck) : null;
  const verdictLabel = benefitsCheck ? (VERDICT_LABELS[benefitsCheck.status] ?? badge?.label) : undefined;
  const checkedAt = date(benefitsCheck?.checkedAt);
  const amountsAt = date(benefitsCheck?.benefitsCheckedAt) ?? checkedAt;

  let notice: string | null = null;
  if (outcome === 'checked_recently') {
    notice = `Already checked${checkedAt ? ` on ${checkedAt.toLocaleDateString()}` : ' recently'}. Showing that result.`;
  } else if (outcome === 'rate_limited' && typeof response.retryAfterSeconds === 'number') {
    notice = `Try again in ${waitPhrase(response.retryAfterSeconds)}.`;
  }

  const payer = benefitsCheck?.payerName?.trim();
  const plan = benefitsCheck?.planName?.trim();
  return {
    title: TITLES[outcome] ?? 'Benefits check',
    verdict: showsResult && badge && verdictLabel ? { label: verdictLabel, color: badge.color } : null,
    notice,
    message: response.message,
    payerPlan: showsResult && (payer || plan) ? [payer, plan].filter(Boolean).join(' · ') : null,
    groups: showsResult && benefitsCheck?.benefits ? amountGroups(benefitsCheck.benefits) : [],
    asOf: showsResult && amountsAt ? `As of ${amountsAt.toLocaleDateString()}` : null,
  };
};
